import React from 'react';
import styled from '@emotion/native';
import domStyled from '@emotion/styled';
import { Image } from 'react-native';
import { BodyText, H2 } from '@/shared/components/StyledText';
import { green, sand, welcomeMutedOnGreen } from '@/shared/styles/colors';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import { applyToBeStylist } from '@/shared/utils';
import {
  FONT_STYLES,
  WELCOME_SECTION_BODY_COPY,
  WELCOME_SECTION_VERTICAL_STYLISTS_CALLOUT,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_GAP_NARROW,
  WELCOME_SECTION_GAP_WIDE,
  WELCOME_SECTION_MAX_WIDTH,
  STYLISTS_CALLOUT_ASPECT_RATIO,
} from '@/shared/styles/constants';
import WelcomeCta from '@/components/welcome/WelcomeCta';

import StylistsCallout from '@/assets/images/StylistsCallout.png';

/** Display cap on wide layout; asset can be larger for sharp scaling. */
const STYLISTS_CALLOUT_IMAGE_MAX_WIDTH = 465;

interface LayoutProps {
  isWideLayout: boolean;
}

const WelcomeStylistsCalloutSection = () => {
  const { isWide: isWideLayout } = useWelcomeSectionLayout();

  return (
    <Section isWideLayout={isWideLayout}>
      <SectionInner isWideLayout={isWideLayout}>
        <CopyColumn isWideLayout={isWideLayout}>
          <SectionTitle isWideLayout={isWideLayout}>
            crafted for stylists
          </SectionTitle>
          <BodyCopy isWideLayout={isWideLayout}>
            EMME gives independent stylists everything they need to run their
            business in one place — client sessions, invoicing, messaging,
            private notes, and additional tooling designed to help you grow.
          </BodyCopy>
          <WelcomeCta
            onPress={applyToBeStylist}
            accessibilityLabel="Apply to be a stylist"
            alignSelf={isWideLayout ? 'flex-end' : 'center'}
          >
            Apply To Be a Stylist
          </WelcomeCta>
        </CopyColumn>
        <ImageCell isWideLayout={isWideLayout}>
          <ImageFrame>
            <CalloutImageAspect>
              <CalloutImage
                source={StylistsCallout}
                alt="Fashion collage showcasing styling work"
                accessibilityLabel="Fashion collage showcasing styling work"
                resizeMode="contain"
              />
            </CalloutImageAspect>
          </ImageFrame>
        </ImageCell>
      </SectionInner>
    </Section>
  );
};

const Section = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  backgroundColor: green,
  paddingTop: WELCOME_SECTION_VERTICAL_STYLISTS_CALLOUT,
  paddingBottom: WELCOME_SECTION_VERTICAL_STYLISTS_CALLOUT,
  paddingHorizontal: isWideLayout
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
}));

const SectionInner = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  maxWidth: isWideLayout ? WELCOME_SECTION_MAX_WIDTH : undefined,
  marginLeft: isWideLayout ? 'auto' : 0,
  marginRight: isWideLayout ? 'auto' : 0,
  flexDirection: isWideLayout ? 'row' : 'column',
  alignItems: isWideLayout ? 'center' : 'stretch',
  justifyContent: isWideLayout ? 'center' : undefined,
  gap: isWideLayout ? WELCOME_SECTION_GAP_WIDE : WELCOME_SECTION_GAP_NARROW,
}));

const ImageCell = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  flex: isWideLayout ? 1 : undefined,
  minWidth: 0,
  maxWidth: isWideLayout ? STYLISTS_CALLOUT_IMAGE_MAX_WIDTH : undefined,
  alignSelf: isWideLayout ? undefined : 'center',
}));

const ImageFrame = styled.View({
  width: '100%',
  borderRadius: 14,
  overflow: 'hidden',
  backgroundColor: green,
});

const CalloutImageAspect = styled.View({
  width: '100%',
  aspectRatio: STYLISTS_CALLOUT_ASPECT_RATIO,
});

const CalloutImage = styled(Image)({
  width: '100%',
  height: '100%',
});

const CopyColumn = styled.View<LayoutProps>(({ isWideLayout }) => ({
  flexShrink: 0,
  minWidth: 0,
  maxWidth: isWideLayout ? 480 : undefined,
  flex: isWideLayout ? 1 : undefined,
  alignItems: isWideLayout ? 'flex-end' : 'center',
}));

const SectionTitle = styled(H2)<LayoutProps>(({ isWideLayout }) => ({
  color: sand,
  textAlign: isWideLayout ? 'right' : 'center',
  textTransform: 'uppercase',
  marginBottom: 20,
  letterSpacing: 1,
  width: '100%',
}));

const BodyCopy = styled(BodyText)<LayoutProps>(({ isWideLayout }) => ({
  color: welcomeMutedOnGreen,
  ...FONT_STYLES.welcomeHelveticaNeue,
  fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
  fontWeight: '300',
  letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
  lineHeight: 24,
  textAlign: isWideLayout ? 'right' : 'center',
  marginBottom: 16,
  width: '100%',
}));

export default WelcomeStylistsCalloutSection;
